import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Popover,
  useTheme,
  useMediaQuery,
  SelectChangeEvent,
} from "@mui/material";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { DateRange } from "react-date-range";
import { RangeKeyDict } from "react-date-range";
import { es } from "date-fns/locale";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import KingBedIcon from "@mui/icons-material/KingBed";
import SingleBedIcon from "@mui/icons-material/SingleBed";
import {
  DepartamentoCategoria,
  precioPorNocheDeCategoria,
} from "../departments/types/Departamento";
import { FormData, FormErrors } from "./types";
import { Sector } from "@/features/calendar/types/Sector";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { Cliente } from "./hooks/useClienteSearch";
import ClienteAutocomplete from "./components/ClienteAutocomplete";
import NombreClienteFields from "../clients/components/NombreClienteFields";
import TipoClienteSelector from "../clients/components/TipoClienteSelector";
import { Z } from "../../theme/zIndex";
import { getOverlayContainer } from "../../theme/containerHelper";
import { isInternalDepartment } from "../calendar/utils/internalDepartments";
import { formatDateKey } from "../shared/utils/dateUtils";
import { AYUDA_DOCUMENTO_EXTRANJERO, esDocumentoExtranjero } from "../shared/utils/validaciones";
import { useDepartmentUnavailability } from "../calendar/departmentAvailability";

interface TabInformacionProps {
  zonas: Sector[];
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: FormErrors;
  documentoWarning?: string;
  categoriaDepartamento: DepartamentoCategoria | null;
  calcularNoches: () => number;
  handleDateRangeClick: (event: React.MouseEvent<HTMLElement>) => void;
  openDateRange: boolean;
  dateRangeAnchorEl: HTMLElement | null;
  handleDateRangeClose: () => void;
  handleDateRangeChange: (ranges: RangeKeyDict) => void;
  dateRange: {
    startDate: Date;
    endDate: Date;
    key: string;
  };
  handleTextChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (e: SelectChangeEvent<string>) => void;
  loadingZonas: boolean;
  handleClienteSelect: (cliente: Cliente) => void;
  handleClienteClear: () => void;
  hasSelectedCliente: boolean;
  esEmpresa: boolean;
  handleEsEmpresaChange: (esEmpresa: boolean) => void;
  esBooking: boolean;
  handleEsBookingChange: (esBooking: boolean) => void;
  esAirbnb: boolean;
  handleEsAirbnbChange: (esAirbnb: boolean) => void;
}

const TabInformacion: React.FC<TabInformacionProps> = ({
  zonas,
  formData,
  setFormData,
  errors,
  documentoWarning,
  categoriaDepartamento,
  calcularNoches,
  handleDateRangeClick,
  openDateRange,
  dateRangeAnchorEl,
  handleDateRangeClose,
  handleDateRangeChange,
  dateRange,
  handleTextChange,
  handleSelectChange,
  loadingZonas,
  handleClienteSelect,
  handleClienteClear,
  hasSelectedCliente,
  esEmpresa,
  handleEsEmpresaChange,
  esBooking,
  handleEsBookingChange,
  esAirbnb,
  handleEsAirbnbChange,
}) => {
  const theme = useTheme();
  const esReservaExterna = esBooking || esAirbnb;
  const esUsoInterno = isInternalDepartment(formData.departamento);
  const shouldShowDepartmentOption = (depto: string): boolean =>
    !isInternalDepartment(depto) || formData.departamento === depto;
  const availabilityDesde = formData.entrada ? formatDateKey(formData.entrada) : "";
  const availabilityHasta = formData.salida ? formatDateKey(formData.salida) : "";
  const { data: availabilityBlocks = [] } = useDepartmentUnavailability(
    availabilityDesde,
    availabilityHasta
  );
  const blockedDepartments = React.useMemo(
    () => new Set(availabilityBlocks.map((block) => block.departamento)),
    [availabilityBlocks]
  );

  // Si el departamento elegido pasa a estar fuera de servicio para esas fechas,
  // se suelta la selección y se dice por qué: un campo vacío sin explicación confunde.
  const [avisoFueraDeServicio, setAvisoFueraDeServicio] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (formData.departamento && blockedDepartments.has(formData.departamento)) {
      setAvisoFueraDeServicio(
        `El Depto ${formData.departamento} está fuera de servicio en esas fechas. Elegí otro.`
      );
      setFormData((current) => ({ ...current, departamento: "" }));
    }
  }, [blockedDepartments, formData.departamento, setFormData]);
  React.useEffect(() => {
    if (formData.departamento) setAvisoFueraDeServicio(null);
  }, [formData.departamento]);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 1.15 }}
      sx={{
        alignContent: "flex-start",
        "& .MuiOutlinedInput-root": {
          minHeight: { xs: 38, sm: 36 },
        },
        "& .MuiInputBase-input": {
          py: { xs: 0.95, sm: 1 },
          fontSize: { xs: "0.84rem", sm: "0.875rem" },
        },
        "& .MuiInputLabel-root": {
          fontSize: { xs: "0.82rem", sm: "0.875rem" },
        },
      }}
    >
      {esUsoInterno && (
        <Grid item xs={12}>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1, sm: 1 },
              borderColor: "rgba(100, 116, 139, 0.35)",
              bgcolor: "rgba(100, 116, 139, 0.08)",
            }}
          >
            <Typography variant="body2" fontWeight={700} color="text.primary">
              Boedo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Permite superposiciones y no genera precio, seña, saldo,
              limpieza ni estadísticas comerciales.
            </Typography>
          </Paper>
        </Grid>
      )}
      {/* Fila 1: Selector de fechas */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          size="small"
          label="Fechas de estadía"
          value={`${dateRange.startDate.toLocaleDateString("es-AR", {
            day: "numeric",
            month: "short",
          })} - ${dateRange.endDate.toLocaleDateString("es-AR", {
            day: "numeric",
            month: "short",
          })}`}
          onClick={handleDateRangeClick}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarMonthIcon
                  sx={{ color: "primary.main", fontSize: "1.1rem" }}
                />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
          }}
        />
        <Popover
          open={openDateRange}
          anchorEl={dateRangeAnchorEl}
          onClose={handleDateRangeClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          sx={{
            zIndex: Z.datepicker,
            "& .MuiPopover-paper": {
              maxWidth: isMobile ? "350px" : isTablet ? "650px" : "700px",
              width: "100%",
              minWidth: isMobile ? "320px" : isTablet ? "600px" : "650px",
              backgroundColor: theme.palette.background.paper,
              zIndex: Z.datepicker,
            },
          }}
        >
          <Box
            sx={{
              p: isMobile ? 1 : 2,
              backgroundColor: theme.palette.background.paper,
              // Atenuar días pasivos para mantener consistencia visual
              "& .rdrDayPassive": {
                opacity: 0.4,
                pointerEvents: "none",
              },
              "& .rdrDayPassive .rdrDayNumber span": {
                color: theme.palette.text.disabled,
              },
            }}
          >
            <DateRange
              ranges={[dateRange]}
              onChange={handleDateRangeChange}
              months={isMobile ? 1 : 2}
              direction={isMobile ? "vertical" : "horizontal"}
              locale={es}
              showDateDisplay={false}
              rangeColors={[theme.palette.primary.main]}
              monthDisplayFormat="MMM yyyy"
              showPreview={false}
              minDate={new Date(2024, 0, 1)}
              sx={{
                "& .rdrMonth": {
                  width: isMobile ? "280px" : "320px",
                },
                "& .rdrCalendarWrapper": {
                  fontSize: isMobile ? "12px" : "14px",
                },
                "& .rdrMonthAndYearWrapper": {
                  fontSize: isMobile ? "14px" : "16px",
                },
                // Reforzar estilo gris para días pasivos dentro del componente
                "& .rdrDays .rdrDayPassive": {
                  opacity: 0.4,
                  pointerEvents: "none",
                },
                "& .rdrDayPassive .rdrDayNumber span": {
                  color: theme.palette.text.disabled,
                },
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDateRangeClose}
                size="small"
              >
                Aplicar
              </Button>
            </Box>
          </Box>
        </Popover>
      </Grid>

      {/* Fila 2: Departamento y Noches */}
      <Grid item xs={6} sm={8}>
        <FormControl
          fullWidth
          error={!!errors.departamento}
          required
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
            "& .MuiMenu-paper": {
              zIndex: Z.popover,
            },
          }}
        >
          <InputLabel id="departamento-label">Departamento</InputLabel>
          <Select
            labelId="departamento-label"
            name="departamento"
            value={formData.departamento || ""}
            onChange={handleSelectChange}
            label="Departamento"
            MenuProps={{
              container: getOverlayContainer,
              sx: {
                zIndex: Z.popover,
                "& .MuiPaper-root": {
                  zIndex: Z.popover,
                },
              },
            }}
            startAdornment={
              <InputAdornment position="start">
                <ApartmentIcon sx={{ color: "primary.main" }} />
              </InputAdornment>
            }
          >
            {loadingZonas ? (
              <MenuItem disabled>Cargando departamentos...</MenuItem>
            ) : zonas && zonas.length > 0 ? (
              zonas.map((sector: Sector) => {
                const departamentosVisibles = sector.departamentos.filter(
                  shouldShowDepartmentOption
                );

                if (departamentosVisibles.length === 0) return [];

                return [
                <MenuItem
                  key={sector.id}
                  disabled
                  divider
                  sx={{
                    bgcolor: theme.palette.background.default,
                    fontWeight: "bold",
                    color: "primary.main",
                  }}
                >
                  Sector {sector.nombre}
                </MenuItem>,
                ...departamentosVisibles.map((depto: string) => (
                  <MenuItem
                    key={depto}
                    value={depto}
                    disabled={blockedDepartments.has(depto)}
                    sx={{ pl: 4 }}
                  >
                    {depto}{blockedDepartments.has(depto) ? " · Fuera de servicio" : ""}
                  </MenuItem>
                )),
              ];
              })
            ) : (
              <MenuItem disabled>No hay departamentos disponibles</MenuItem>
            )}
          </Select>
          {errors.departamento && (
            <FormHelperText>{errors.departamento}</FormHelperText>
          )}
          {!errors.departamento && avisoFueraDeServicio && (
            <FormHelperText sx={{ color: "text.secondary" }}>{avisoFueraDeServicio}</FormHelperText>
          )}
        </FormControl>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            height: { xs: 38, sm: 36 },
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            bgcolor: "background.default",
            gap: 0.5,
          }}
        >
          <Typography variant="subtitle1" color="primary" fontWeight="bold">
            {calcularNoches()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            noche{calcularNoches() !== 1 ? "s" : ""}
          </Typography>
        </Paper>
      </Grid>

      {/* Fila 3: Cantidad de huéspedes */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.72rem", sm: "0.75rem" } }}
          >
            Cantidad de huéspedes
          </Typography>
          <ToggleButtonGroup
            value={formData.cantidad_huespedes || 1}
            exclusive
            onChange={(e, value) => {
              if (value !== null) {
                setFormData((prev) => ({
                  ...prev,
                  cantidad_huespedes: value,
                }));
              }
            }}
            fullWidth
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 0.25,
                px: 0.25,
                fontSize: { xs: "0.8rem", sm: "0.85rem" },
                border: "1px solid",
                borderColor: "divider",
                height: { xs: 34, sm: 32 },
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                },
              },
            }}
          >
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <ToggleButton key={num} value={num}>
                {num}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Grid>

      {/* Fila 4: Tipo de cama */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.72rem", sm: "0.75rem" } }}
          >
            Tipo de cama
          </Typography>
          <ToggleButtonGroup
            value={formData.tipo_cama || []}
            onChange={(e, value) => {
              setFormData((prev) => ({ ...prev, tipo_cama: value || [] }));
            }}
            fullWidth
            size="small"
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                py: 0.5,
                px: { xs: 0.35, sm: 0.5 },
                border: "1px solid",
                borderColor: "divider",
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0.35, sm: 0.5 },
                height: { xs: 38, sm: 36 },
                bgcolor: "background.paper",
                color: "text.primary",
                textTransform: "none",
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                },
              },
            }}
          >
            <ToggleButton value="doble">
              <KingBedIcon sx={{ fontSize: { xs: 17, sm: 20 } }} />
              <Typography
                variant="caption"
                noWrap
                sx={{ ml: { xs: 0.5, sm: 0.5 }, fontSize: { xs: "0.78rem", sm: "0.75rem" } }}
              >
                Cama doble
              </Typography>
            </ToggleButton>
            <ToggleButton value="individual">
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <SingleBedIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                <SingleBedIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
              </Box>
              <Typography
                variant="caption"
                noWrap
                sx={{ ml: { xs: 0.5, sm: 0.5 }, fontSize: { xs: "0.78rem", sm: "0.75rem" } }}
              >
                Individuales
              </Typography>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

      {/* Fila 5: Sección de tarifa */}
      {!esUsoInterno && (
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{
            color: "primary.main",
            display: "inline-block",
            mb: { xs: 0.15, sm: 0.25 },
            fontSize: { xs: "0.82rem", sm: "0.9rem" },
          }}
        >
          <AttachMoneyIcon
            sx={{ mr: 0.5, verticalAlign: "middle", fontSize: "1.1rem" }}
          />
          Tarifa
        </Typography>
      </Grid>
      )}

      {!esUsoInterno && (
      <Grid item xs={12}>
        <Box>
          <Grid container spacing={{ xs: 1, sm: 1 }}>
            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Valor por noche"
                value={(() => {
                  if (!categoriaDepartamento) return "";
                  const valor = precioPorNocheDeCategoria(
                    categoriaDepartamento,
                    calcularNoches()
                  );
                  return valor || "";
                })()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 38, sm: 36 },
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Precio Personalizado"
                name="precioPersonalizado"
                type="number"
                value={formData.precioPersonalizado || ""}
                onChange={handleTextChange}
                placeholder="Opcional"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 38, sm: 36 },
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Noches"
                value={calcularNoches()}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonthIcon
                        sx={{ color: "primary.main", fontSize: "1.1rem" }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: { xs: 38, sm: 36 },
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Grid>
      )}

      {/* Campo de valor total */}
      {!esUsoInterno && (
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Valor Total"
          value={formData.valorTotal || 0}
          InputProps={{
            readOnly: true,
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
          }}
        />
      </Grid>
      )}

      {/* Campo de seña */}
      {!esUsoInterno && (
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          label="Seña"
          name="sena"
          type="number"
          value={formData.sena || 0}
          onChange={handleTextChange}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
          }}
        />
      </Grid>
      )}

      {/* Fila 6: Titular de la reserva */}
      <Grid item xs={12}>
        <Typography
          variant="subtitle2"
          fontWeight="bold"
          sx={{
            color: "primary.main",
            display: "inline-block",
            mb: { xs: 0.15, sm: 0.25 },
            fontSize: { xs: "0.82rem", sm: "0.9rem" },
          }}
        >
          <PersonIcon
            sx={{ mr: 0.5, verticalAlign: "middle", fontSize: "1.1rem" }}
          />
          Titular de la reserva
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: { xs: "none", sm: "block" },
            ml: 3.5,
            mb: 0.25,
          }}
        >
          Datos obligatorios
        </Typography>
      </Grid>

      <Grid item xs={12}>
        <ClienteAutocomplete
          onSelect={handleClienteSelect}
          onClear={handleClienteClear}
          hasSelectedCliente={hasSelectedCliente}
          titularFieldsLocked={hasSelectedCliente}
          esBooking={esBooking}
          onEsBookingChange={handleEsBookingChange}
          esAirbnb={esAirbnb}
          onEsAirbnbChange={handleEsAirbnbChange}
          errors={errors}
        />
      </Grid>

      <Grid item xs={12}>
        <TipoClienteSelector
          esEmpresa={esEmpresa}
          onChange={handleEsEmpresaChange}
          disabled={hasSelectedCliente}
        />
      </Grid>

      <NombreClienteFields
        esEmpresa={esEmpresa}
        nombre={formData.nombre || ""}
        apellido={formData.apellido || ""}
        onChange={handleTextChange}
        errores={{ nombre: errors.nombre, apellido: errors.apellido }}
        bloqueado={{ nombre: hasSelectedCliente, apellido: hasSelectedCliente }}
        estilo="placeholder"
        icono={<PersonIcon sx={{ color: "primary.main", fontSize: "1.1rem" }} />}
        columnas={{ xs: 6 }}
        onPasarAEmpresa={
          hasSelectedCliente || esBooking || esAirbnb ? undefined : () => handleEsEmpresaChange(true)
        }
      />

      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          placeholder={
            esBooking
              ? "Código de reserva Booking"
              : esAirbnb
                ? "Código de reserva Airbnb"
                : esEmpresa
                  ? "CUIT *"
                  : "DNI *"
          }
          name="dni"
          value={formData.dni || ""}
          onChange={handleTextChange}
          disabled={hasSelectedCliente}
          error={!!errors.dni}
          helperText={
            errors.dni ||
            documentoWarning ||
            (!esReservaExterna && esDocumentoExtranjero(formData.dni || "") ? AYUDA_DOCUMENTO_EXTRANJERO : undefined)
          }
          FormHelperTextProps={{
            sx: !errors.dni && documentoWarning ? { color: "warning.main" } : undefined,
          }}
          required
          inputProps={{ "aria-label": "Documento" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonIcon
                  sx={{ color: "primary.main", fontSize: "1.1rem" }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {!esReservaExterna && (
        <Grid item xs={6}>
          <TextField
            fullWidth
            size="small"
            placeholder="Teléfono principal *"
            name="telefono1"
            value={formData.telefono1 || ""}
            onChange={handleTextChange}
            disabled={hasSelectedCliente}
            error={!!errors.telefono1}
            helperText={errors.telefono1}
            required
            inputProps={{ "aria-label": "Teléfono principal" }}
            sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: "primary.main", fontSize: "1.1rem" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      )}
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          placeholder="Email"
          name="email1"
          value={formData.email1 || ""}
          onChange={handleTextChange}
          disabled={hasSelectedCliente}
          error={!!errors.email1}
          helperText={errors.email1}
          inputProps={{ "aria-label": "Email" }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: "primary.main", fontSize: "1.1rem" }} />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <TextField
          fullWidth
          size="small"
          placeholder="Localidad"
          name="localidad"
          value={formData.localidad || ""}
          onChange={handleTextChange}
          disabled={hasSelectedCliente}
          error={!!errors.localidad}
          helperText={errors.localidad}
          inputProps={{ "aria-label": "Localidad" }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: { xs: 38, sm: 36 },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOnIcon
                  sx={{
                    color: "primary.main",
                    fontSize: "1.1rem",
                  }}
                />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default TabInformacion;
